<template>
  <div class="ml-56">
    <!-- Page content -->
    <div class="w-full h-full min-h-screen overflow-y-scroll bg-truegray-100">
      <!-- Page header -->
      <PageHeader :overline="headerConfig.overline" :title="headerConfig.title">
        <template #title>
          <div class="flex items-center space-x-4 w-3/4">
            <div class="px-8 lg:border-l-8 border-truegray-100">
              <h2 class="text-cw-900 font-montserrat mb-10">
                {{ headerConfig.title }}
              </h2>
            </div>
          </div>
        </template>
      </PageHeader>
      <!-- /Page header -->

      <!-- Connections content -->
      <div v-show="user" class="w-full py-16 px-8 md:px-16 lg:px-26 2xl:px-32">
        <!-- Loading -->
        <div
          v-if="isLoading"
          class="h-52 flex flex-col justify-center items-center"
        >
          <lottie :options="loadingAnimationOptions" />
          <span class="text-center">{{ loadingText }}</span>
        </div>
        <!-- /Loading -->

        <!-- Connections -->
        <template v-else>
          <!-- Nav buttons -->
          <div v-if="activeView === VIEW.CONNECTIONS" class="w-full">
            <button
              v-if="canAddConnection"
              class="btn btn-cw-cd-primary flex items-center cw-gradient"
              :disabled="!canAddConnection"
              @click="routeToView"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-6 w-6 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
              {{ addConnectionText }}
            </button>
            <div v-else-if="!isContributorAdminConnectionsOnly" class="mb-4">
              <p class="text-red-400 mb-4">
                You have reached your limit for adding new connections. Email
                christina@careerwise.school to continue.
              </p>

              <!--
                <button
                class="btn btn-trans-black btn-cw-cd p-3 mt-6 mb-8 font-normal"
                @click="showConnectionsPackModal"
                >
                <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                class="w-4 h-4 mr-1"
                >
                <path
                d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z"
                />
                </svg>
                Connections
                </button> 
              -->
            </div>
          </div>
          <div v-else>
            <button
              class="btn btn-cw-cd-primary flex items-center cw-gradient"
              @click="routeToView"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                class="h-6 w-6 mr-2"
              >
                <path
                  fill-rule="evenodd"
                  d="M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z"
                  clip-rule="evenodd"
                />
              </svg>
              {{ backButtonText }}
            </button>
          </div>
          <!-- /Nav buttons -->

          <!-- Upsert form -->
          <FormUpsertConnection
            v-if="activeView === VIEW.UPSERT"
            classes="bg-white p-12 rounded-lg"
          />
          <!-- /Upsert form -->

          <!-- Connections cards -->
          <div
            v-else-if="connections.length"
            :class="[
              'grid grid-cols-1 md:grid-cols-2 gap-6',
              {
                '2xl:grid-cols-3': !isContributorAdminConnectionsOnly,
              },
            ]"
          >
            <CardConnection
              v-for="connection in connections"
              :key="connection.id"
              :connection="connection"
              :context="'CONTRIBUTOR'"
            />
          </div>
          <p v-else-if="!isContributorAdminConnectionsOnly" class="text-md">
            We could not find any connections...
          </p>
          <!-- /Connections cards -->
        </template>
        <!-- /Connections -->

        <!-- Pagination -->
        <div
          v-if="pagination.previouspage || pagination.nextpage"
          class="flex justify-between items-center h-10 mt-8"
        >
          <span
            :disabled="!pagination.previouspage"
            :class="[
              !pagination.previouspage
                ? 'opacity-25 cursor-not-allowed'
                : 'hover:text-gray-900 cursor-pointer transition ease-in-out duration-200',
            ]"
            class="text-gray-500"
            @click="paginateConnections(pagination.previouspage)"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M11 17l-5-5m0 0l5-5m-5 5h12"
              />
            </svg>
          </span>
          <span class="text-center text-xs"
            >Page {{ pagination.current_page }} of
            {{ pagination.total_pages }}</span
          >
          <span
            :disabled="!pagination.nextpage"
            :class="[
              !pagination.nextpage
                ? 'opacity-25 cursor-not-allowed'
                : 'hover:text-gray-900 cursor-pointer transition ease-in-out duration-200',
            ]"
            class="text-gray-500"
            @click="paginateConnections(pagination.nextpage)"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 7l5 5m0 0l-5 5m5-5H6"
              />
            </svg>
          </span>
        </div>
        <!-- /Pagination -->
      </div>
      <!-- /Connections content -->
    </div>
    <!-- /Page content -->
  </div>
</template>

<script>
import { EventBus } from '../../../js/plugins/event-bus.js'
import { defineComponent, computed, ref, onMounted, set, watch } from 'vue'
import { useStore } from '../../../js/store/index.js'
import { useRoute, useRouter } from 'vue-router/composables'
import CardConnection from '../../shared/connections/components/CardConnection.vue'
import FormUpsertConnection from '../../shared/connections/FormUpsertConnection.vue'
import PageHeader from '../components/PageHeader.vue'
import * as loadingAnimation from '../../../shared/components/loading-dark.json'
import { EVENT_KEYS } from '../../../js/util/constants.js'

export default defineComponent({
  name: 'Connections',

  components: {
    PageHeader,
    FormUpsertConnection,
    CardConnection,
  },

  setup() {
    const store = useStore()
    const route = useRoute()
    const router = useRouter()
    const user = computed(() => store.getters['shared/getUser'])

    const headerConfig = computed(() => {
      return {
        overline: 'connections',
        title: isContributorAdminConnectionsOnly.value
          ? `My ${organisation.value?.title} Connection`
          : `${organisation.value?.title} Connections`,
      }
    })

    const loadingAnimationOptions = { animationData: loadingAnimation.default }
    const isLoading = ref(false)
    const globalLoading = computed(() => store.getters['shared/isLoading'])
    watch(globalLoading, (val) => {
      isLoading.value = val
    })
    const loadingText = computed(() => {
      return activeView.value === VIEW.CONNECTIONS
        ? 'Loading connections'
        : 'Loading form'
    })

    // VIEWS/ROUTES/TABS
    const VIEW = {
      CONNECTIONS: 'connections',
      UPSERT: 'upsert',
    }
    const ROUTES_UPSERT = [
      'ContributorsAddConnection',
      'ContributorsUpdateConnection',
    ]

    const backButtonText = computed(() => {
      if (isContributorAdminConnectionsOnly.value) {
        return 'View my connection'
      }

      return 'Back to connections'
    })

    const activeView = ref(VIEW.CONNECTIONS)
    const changeView = (newView = null) => {
      const toggledView =
        activeView.value === VIEW.CONNECTIONS ? VIEW.UPSERT : VIEW.CONNECTIONS
      newView = newView || toggledView
      activeView.value = newView
    }

    const routeToView = () => {
      const name =
        activeView.value === VIEW.CONNECTIONS
          ? ROUTES_UPSERT[0]
          : 'ContributorsConnections'
      if (route.name !== name) {
        router.push({ name })
      }
    }

    const watchRoute = async () => {
      const value = ROUTES_UPSERT.includes(route.name)
        ? VIEW.UPSERT
        : VIEW.CONNECTIONS
      changeView(value)
      await updateConnections()
    }

    watch(route, () => watchRoute())

    const connectionsFilters = ref(null)
    const pagination = ref({
      current_page: null,
      total_pages: null,
      per_page: 9,
      next_page: null,
      previous_page: null,
      last_page_url: null,
      next_page_url: null,
      prev_page_url: null,
      path: null,
      nextpage: null,
      previouspage: null,
    })

    const isContributorAdminConnectionsOnly = computed(() => {
      return store.getters['shared/isContributorAdminConnectionsOnly']
    })

    const organisation = computed(() => {
      return store.getters['contributors/getOrganisation']
    })

    const organisationId = computed(
      () => store.getters['contributors/getOrganisationId'],
    )
    watch(organisationId, () => {
      // If this organisation cannot add connections, kick out of the form!!
      if (activeView.value === VIEW.UPSERT) {
        routeToView()
      }
    })

    const connections = computed(() => {
      return store.getters['contributors/getConnections']
    })

    const canAddConnection = computed(() => {
      return store.getters['contributors/canAddConnection']
    })

    const addConnectionText = computed(() => {
      if (isContributorAdminConnectionsOnly.value) {
        return 'Create My Connection'
      }

      return 'New Connection'
    })

    const showConnectionsPackModal = () => {
      EventBus.$emit('showModal', {
        action: 'cw-contributor-connections-pack',
      })
    }

    const submitFilters = async (payload, callback) => {
      connectionsFilters.value = payload
      await updateConnections()
      callback()
    }

    const updatePagination = (pag) => {
      // Pagination total = ('total # of connections' divided by 'per_page') + 1
      // Pagination doesn't show when not needed so +1 is never wrong on single page
      const total_pages = Math.floor(pag.total / pag.per_page) + 1
      set(pagination.value, 'current_page', pag.current_page)
      set(pagination.value, 'total_pages', total_pages)
      set(pagination.value, 'per_page', pag.per_page)
      set(pagination.value, 'last_page_url', pag.last_page_url)
      set(pagination.value, 'next_page_url', pag.next_page_url)
      set(pagination.value, 'prev_page_url', pag.prev_page_url)
      set(pagination.value, 'next_page_path', pag.next_page_path)

      let prev = null
      if (pagination.value.prev_page_url) {
        prev = pagination.value.prev_page_url.split('=')
        prev = prev[1]
      }

      let next = null
      if (pagination.value.next_page_url) {
        next = pagination.value.next_page_url.split('=')
        next = next[1]
      }

      set(pagination.value, 'previouspage', prev)
      set(pagination.value, 'nextpage', next)
    }

    const paginateConnections = (page) => {
      updateConnections({ page })
    }

    const updateConnections = async ({
      page = 1, // connectionsPagination.value.current_page ?
      filters = connectionsFilters.value,
    } = {}) => {
      isLoading.value = true
      const payload = {
        page,
        filters,
      }

      const pagination = await store.dispatch(
        'contributors/dispatchInitPageConnections',
        payload,
      )

      if (pagination) {
        updatePagination(pagination)
      }

      isLoading.value = false
    }

    const clearFilters = async () => {
      connectionsFilters.value = null
      EventBus.$emit('clearConnectionsFilters')
    }

    onMounted(async () => {
      await watchRoute()
      clearFilters()

      EventBus.$on(EVENT_KEYS.CO_UPDATE_COMPONENT_PAGINATION, (paginationObj) =>
        updatePagination(paginationObj),
      )
    })

    return {
      // DATA
      loadingAnimationOptions,
      isLoading,
      loadingText,
      headerConfig,

      // VIEWS
      VIEW,
      activeView,
      routeToView,
      changeView,
      backButtonText,

      user,
      isContributorAdminConnectionsOnly,
      organisation,
      pagination,
      connections,
      submitFilters,
      paginateConnections,
      updateConnections,
      canAddConnection,
      addConnectionText,
      showConnectionsPackModal,
    }
  },
})
</script>
